import { KeycloakAuthorizationInstance } from "keycloak-js/dist/keycloak-authz";
import { useMemo } from "react";
import { useKeycloakAuthz } from "../components/keycloak-provider";
import { isJWTTokenExpired } from "./auth";

export const BACKOFFICE_RESOURCE = "backoffice";
export const ACCESS_DENIED = new Error("Access Denied.");

interface CachedRptToken {
  token: string;
  timestamp: number;
}

// cache must be global to persist across service instances
const rptCache: Map<string, CachedRptToken> = new Map();

export class AuthorizationService {
  constructor(private readonly authz: KeycloakAuthorizationInstance) {}

  /**
   * Gets an RPT token for a specific property, using cache when available and valid
   * @param propertyTicker The property ticker/identifier
   * @returns Promise<string> The RPT token
   */
  async getRptToken(propertyTicker: string): Promise<string> {
    // Check if we have a cached token that's still valid
    const cachedToken = rptCache.get(propertyTicker);
    if (cachedToken && !this.isTokenExpired(cachedToken.token)) {
      return cachedToken.token;
    }

    // Reset RPT to force a new request
    this.authz.rpt = null;

    try {
      const rptToken = await new Promise<string>((resolve, reject) =>
        this.authz
          .entitlement(BACKOFFICE_RESOURCE, {
            permissions: [
              {
                id: `property:${propertyTicker}`,
              },
            ],
          })
          .then(
            rpt => resolve(rpt),
            () => reject(ACCESS_DENIED),
            () => reject(new Error("Authorization error"))
          )
      );

      // Cache the new token
      this.cacheToken(propertyTicker, rptToken);
      return rptToken;
    } catch (error) {
      // Remove expired/invalid token from cache
      rptCache.delete(propertyTicker);
      throw error;
    }
  }

  /**
   * Clears the RPT token cache for a specific property or all properties
   * @param propertyTicker Optional property ticker. If not provided, clears all cache
   */
  clearCache(propertyTicker?: string): void {
    if (propertyTicker) {
      rptCache.delete(propertyTicker);
    } else {
      rptCache.clear();
    }
  }

  /**
   * Gets all cached property tickers
   * @returns string[] Array of cached property tickers
   */
  getCachedProperties(): string[] {
    return Array.from(rptCache.keys());
  }

  /**
   * Checks if a cached token exists for a property and is still valid
   * @param propertyTicker The property ticker
   * @returns boolean True if valid cached token exists
   */
  hasValidCachedToken(propertyTicker: string): boolean {
    const cachedToken = rptCache.get(propertyTicker);
    return cachedToken !== undefined && !this.isTokenExpired(cachedToken.token);
  }

  private cacheToken(propertyTicker: string, token: string): void {
    rptCache.set(propertyTicker, {
      token,
      timestamp: Date.now(),
    });
  }

  private isTokenExpired(token: string): boolean {
    try {
      return isJWTTokenExpired(token);
    } catch (error) {
      console.error("Error checking token expiration", error);
      return true; // Treat invalid tokens as expired
    }
  }
}

export const useAuthorizationService = (): AuthorizationService => {
  const keycloakAuthz = useKeycloakAuthz();

  return useMemo(() => new AuthorizationService(keycloakAuthz), [keycloakAuthz]);
};
